import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiExclamation, HiInformationCircle } from "react-icons/hi";
import { ModalShell } from "../atoms/ModalShell";
import { CustomButton } from "../atoms/CustomButton";
import { Spinner } from "../atoms/Spinner";
import BillingAddressFields, {
  isBillingAddressComplete,
} from "./BillingAddressFields";
import {
  classifyPlanChange,
  immediateAlternative,
  PaidTier,
  planInfo,
} from "./planCatalog";
import { usePlanChangeFormat } from "./usePlanChangeFormat";
import PlanChangeService, {
  BillingAddress,
  newRequestId,
  parsePayModDate,
  PlanChange,
  planChangeRefusal,
  PlanChangePreview,
  PlanChangeResult,
} from "../../services/planChange";
import {
  forgetPlanChanges,
  useApplyPlanChange,
  usePreviewPlanChange,
  useSaveBillingAddress,
} from "../../hooks/billing/planChange";
import { forgetOwnerPlans, useCardUpdateLink } from "../../hooks/billing";
import { usePaymentVerification } from "../../hooks/usePaymentVerification";
import {
  comparePlanFeatures,
  LEAD_LIMITS,
  RATE_LIMITS,
  TODO_BOARD_LIMITS,
  USER_LIMITS,
} from "../../utils/permissions";

type Step = "loading" | "address" | "review" | "payment" | "error";

const KNOWN_LIMITS = ["customers", "team_members", "leads", "todo_boards"];

const PlanChangeModal = ({
  customerId,
  entityId,
  fromPlan,
  toPlan,
  ownerCountryCode,
  scheduledChange,
  onClose,
  onDone,
}: {
  customerId: string;
  entityId: string;
  fromPlan: string;
  toPlan: string;
  ownerCountryCode?: string;
  scheduledChange?: PlanChange | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  const { k, money, count, date, failure, tierName, planName } =
    usePlanChangeFormat();
  const { forcePaymentCheck } = usePaymentVerification();

  const [target, setTarget] = useState(toPlan);
  const [step, setStep] = useState<Step>("loading");
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [requestId, setRequestId] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PlanChangeResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [address, setAddress] = useState<BillingAddress>({
    line1: "",
    line2: "",
    postal_code: "",
    city: "",
    state: "",
    country: (ownerCountryCode || "").toUpperCase(),
  });
  const [showMissing, setShowMissing] = useState(false);

  const { mutate: requestPreview } = usePreviewPlanChange();
  const { mutate: applyChange, isLoading: isApplying } = useApplyPlanChange();
  const { mutate: saveAddress, isLoading: isSavingAddress } =
    useSaveBillingAddress();
  const { mutate: requestCardUpdateLink, isLoading: isOpeningCardUpdate } =
    useCardUpdateLink();

  const failureRef = useRef(failure);
  failureRef.current = failure;

  const runPreview = useCallback(
    (to: string) => {
      setStep("loading");
      setError(null);
      setPreview(null);
      requestPreview(
        { customerId, entityId, from_plan: fromPlan, to_plan: to },
        {
          onSuccess: (data) => {
            setPreview(data);
            setRequestId(newRequestId());
            setStep("review");
          },
          onError: (err) => {
            if (planChangeRefusal(err) === "BILLING_ADDRESS_REQUIRED") {
              setStep("address");
              return;
            }
            setError(failureRef.current(err));
            setStep("error");
          },
        }
      );
    },
    [customerId, entityId, fromPlan, requestPreview]
  );

  useEffect(() => {
    runPreview(target);
  }, [target, runPreview]);

  const timing =
    preview?.timing ??
    (classifyPlanChange(fromPlan, target) === "UPGRADE"
      ? "IMMEDIATE"
      : "PERIOD_END");
  const isImmediate = timing === "IMMEDIATE";
  const scheduled =
    scheduledChange?.status === "SCHEDULED" ? scheduledChange : null;
  const blockedByScheduled = !isImmediate && Boolean(scheduled);
  const alternative = !isImmediate
    ? immediateAlternative(fromPlan, target)
    : null;

  const fromInfo = planInfo(fromPlan);
  const toInfo = planInfo(target);

  const finish = async (applied: PlanChangeResult) => {
    forgetOwnerPlans(entityId);
    forgetPlanChanges();
    if (applied.status === "APPLIED") {
      setFinishing(true);
      try {
        await forcePaymentCheck();
      } finally {
        setFinishing(false);
      }
      toast.success(k("toasts.applied", { plan: planName(target) }));
    } else {
      toast.success(
        k("toasts.scheduled", {
          plan: planName(target),
          date: date(applied.effective_at || preview?.effective_at),
        })
      );
    }
    onDone();
  };

  const confirm = () => {
    if (!preview) return;
    setError(null);
    setNotice(null);
    applyChange(
      {
        customerId,
        entityId,
        from_plan: fromPlan,
        to_plan: target,
        proration_date: preview.proration_date,
        request_id: requestId,
      },
      {
        onSuccess: (data) => {
          if (data.status === "APPLIED" || data.status === "SCHEDULED") {
            finish(data);
            return;
          }
          forgetPlanChanges();
          setResult(data);
          setStep("payment");
        },
        onError: (err) => {
          const refusal = planChangeRefusal(err);
          if (refusal === "PRORATION_DATE_EXPIRED") {
            runPreview(target);
            setNotice(k("errors.recalculated"));
            return;
          }
          if (refusal === "BILLING_ADDRESS_REQUIRED") {
            setStep("address");
            return;
          }
          setError(failure(err));
        },
      }
    );
  };

  const submitAddress = () => {
    if (!isBillingAddressComplete(address)) {
      setShowMissing(true);
      setError(k("address.required"));
      return;
    }
    setError(null);
    saveAddress(
      {
        customerId,
        entityId,
        from_plan: fromPlan,
        billing_address: {
          ...address,
          line1: address.line1.trim(),
          line2: address.line2?.trim() || undefined,
          postal_code: address.postal_code?.trim() || undefined,
          city: address.city.trim(),
          state: address.state?.trim() || undefined,
        },
      },
      {
        onSuccess: () => runPreview(target),
        onError: (err) => setError(failure(err)),
      }
    );
  };

  const checkPayment = async () => {
    setChecking(true);
    setNotice(null);
    setError(null);
    try {
      const changes = await PlanChangeService.list(customerId, entityId);
      const awaited = result?.plan_change?.plan_change_identification;
      const change = awaited
        ? changes.find((item) => item.plan_change_identification === awaited)
        : changes.find(
            (item) =>
              item.to_plan_code === target &&
              (item.status === "PENDING_PAYMENT" || item.status === "APPLIED")
          );
      if (change?.status === "APPLIED") {
        await finish({ status: "APPLIED" });
      } else if (
        change &&
        ["EXPIRED", "CANCELED", "FAILED"].includes(change.status)
      ) {
        forgetPlanChanges();
        setError(k("payment.expired"));
      } else {
        setNotice(k("payment.notYetPaid"));
      }
    } catch (err) {
      setError(failure(err));
    } finally {
      setChecking(false);
    }
  };

  const openInvoice = () => {
    if (result?.hosted_invoice_url) {
      window.open(result.hosted_invoice_url, "_blank", "noopener,noreferrer");
    }
  };

  const openCardUpdate = () =>
    requestCardUpdateLink(
      {
        customer_identification: customerId,
        entityIdentification: entityId,
        plan: fromPlan,
      },
      { onSuccess: (link) => window.location.assign(link.url) }
    );

  const lostFeatures = useMemo(() => {
    if (!fromInfo || !toInfo) return [];
    return comparePlanFeatures(toInfo.tier, fromInfo.tier)
      .additionalFeatures.filter((feature) => feature.showInPricingPage)
      .map((feature) => t(`features.${String(feature.feature)}`) as string);
  }, [fromInfo, toInfo, t]);

  const loweredLimits = useMemo(() => {
    if (!fromInfo || !toInfo) return [];
    const rows: Array<{ key: string; from: number; to: number }> = [];
    const add = (key: string, table: Record<PaidTier, number>) => {
      const from = table[fromInfo.tier];
      const to = table[toInfo.tier];
      if (to < from) rows.push({ key, from, to });
    };
    add("customers", USER_LIMITS);
    add("leads", LEAD_LIMITS);
    add("todo_boards", TODO_BOARD_LIMITS);
    add("api_rate", RATE_LIMITS);
    return rows;
  }, [fromInfo, toInfo]);

  const isDowngrade = lostFeatures.length > 0 || loweredLimits.length > 0;

  const limitLabel = (key: string) =>
    KNOWN_LIMITS.includes(key) || key === "api_rate"
      ? k(`limits.${key}`)
      : k("limits.other");

  const nextDate = () => {
    if (!preview) return "";
    if (fromInfo && toInfo && fromInfo.interval !== toInfo.interval) {
      const start = parsePayModDate(preview.effective_at);
      return start.isValid()
        ? date(
            start
              .add(1, toInfo.interval === "annual" ? "year" : "month")
              .toISOString()
          )
        : "";
    }
    return date(preview.current_period_end);
  };

  const title = isImmediate
    ? k("modal.titleUpgrade", { plan: planName(target) })
    : k("modal.titleDowngrade", { plan: planName(target) });

  const renderNote = (tone: "info" | "warning", children: ReactNode) => (
    <div
      className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
        tone === "info"
          ? "border-blue-200 bg-blue-50 text-blue-900"
          : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
    >
      {tone === "info" ? (
        <HiInformationCircle
          className="mt-0.5 shrink-0 text-blue-600"
          size={18}
        />
      ) : (
        <HiExclamation className="mt-0.5 shrink-0 text-amber-600" size={18} />
      )}
      <div className="flex min-w-0 flex-col gap-2">{children}</div>
    </div>
  );

  const renderImmediate = (p: PlanChangePreview) => (
    <div className="flex flex-col gap-4">
      {scheduled &&
        renderNote(
          "warning",
          <p>
            {k("modal.cancelsScheduled", {
              plan: planName(scheduled.to_plan_code),
              date: date(scheduled.effective_at),
            })}
          </p>
        )}

      <dl className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 gap-y-1.5">
        <dt className="text-sm font-medium text-gray-900">
          {k("modal.dueNow")}
        </dt>
        <dd className="text-right text-base font-semibold tabular-nums text-gray-900">
          {money(p.amount_due, p.currency)}
        </dd>
        {p.tax > 0 && (
          <>
            <dt className="text-xs text-gray-500">{k("modal.subtotal")}</dt>
            <dd className="text-right text-xs tabular-nums text-gray-700">
              {money(p.subtotal, p.currency)}
            </dd>
            <dt className="text-xs text-gray-500">{k("modal.tax")}</dt>
            <dd className="text-right text-xs tabular-nums text-gray-700">
              {money(p.tax, p.currency)}
            </dd>
          </>
        )}
      </dl>

      <div className="h-px w-full bg-gray-200" />

      <dl className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 gap-y-1.5">
        <dt className="text-sm text-gray-700">
          {toInfo?.interval === "month"
            ? k("modal.thenMonthly")
            : k("modal.thenAnnually")}
        </dt>
        <dd className="text-right text-sm font-medium tabular-nums text-gray-900">
          {money(p.next_amount, p.currency)}
        </dd>
        {nextDate() && (
          <dt className="col-span-2 text-xs text-gray-500">
            {k("modal.startingOn", { date: nextDate() })}
          </dt>
        )}
      </dl>

      <p className="text-xs text-gray-500">
        {k("modal.creditNote", { plan: tierName(fromPlan) })}
      </p>

      {p.lines && p.lines.length > 0 && (
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer select-none text-gray-700">
            {k("modal.details")}
          </summary>
          <ul className="mt-2 divide-y divide-gray-100">
            {p.lines.map((line, index) => (
              <li
                key={index}
                className="flex items-baseline justify-between gap-4 py-1.5"
              >
                <span className="min-w-0">{line.description}</span>
                <span className="shrink-0 tabular-nums text-gray-900">
                  {money(line.amount, p.currency)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );

  const renderPeriodEnd = (p: PlanChangePreview) => (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-700">
        {k(
          toInfo?.interval === "month"
            ? "modal.keepUntilMonthly"
            : "modal.keepUntilAnnually",
          {
            from: planName(fromPlan),
            to: planName(target),
            date: date(p.effective_at || p.current_period_end),
            amount: money(p.next_amount, p.currency),
          }
        )}
      </p>

      {alternative &&
        renderNote(
          "info",
          <>
            <p>{k("modal.upgradeNowHint", { plan: tierName(alternative) })}</p>
            <div>
              <CustomButton
                variant="secondary"
                size="xs"
                title={k("modal.upgradeNowAction", {
                  plan: tierName(alternative),
                })}
                onClick={() => setTarget(alternative)}
              />
            </div>
          </>
        )}

      {blockedByScheduled &&
        renderNote("warning", <p>{k("modal.otherChangeScheduled")}</p>)}

      {isDowngrade && (
        <div className="flex flex-col gap-3">
          {p.over_limits && (
            <div>
              <h4 className="mb-1 text-sm font-semibold text-gray-900">
                {k("modal.overLimitsTitle", { plan: tierName(target) })}
              </h4>
              {p.over_limits.length === 0 ? (
                <p className="text-xs text-gray-600">
                  {p.unchecked_limits?.length
                    ? k("modal.overLimitsUnchecked")
                    : k("modal.overLimitsNone", { plan: tierName(target) })}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 text-xs text-gray-700">
                  {p.over_limits.map((row) => (
                    <li key={row.key} className="py-1.5">
                      {k("modal.overLimit", {
                        label: limitLabel(row.key),
                        usage: count(row.usage),
                        limit: count(row.limit),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <h4 className="mb-1 text-sm font-semibold text-gray-900">
              {k("modal.lostTitle", { plan: tierName(target) })}
            </h4>
            <ul className="divide-y divide-gray-100 text-xs text-gray-700">
              {loweredLimits.map((row) => (
                <li key={row.key} className="py-1.5">
                  {k("modal.limitLowered", {
                    label: limitLabel(row.key),
                    to: count(row.to),
                    from: count(row.from),
                  })}
                </li>
              ))}
              {lostFeatures.map((feature) => (
                <li key={feature} className="py-1.5">
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-gray-500">{k("modal.nothingDeleted")}</p>
        </div>
      )}
    </div>
  );

  const renderPayment = () => {
    const failed = Boolean(result?.payment_error);
    return (
      <div className="flex flex-col gap-3">
        <h4 className="text-base font-semibold text-gray-900">
          {failed ? k("payment.failedTitle") : k("payment.actionTitle")}
        </h4>
        {failed && (
          <p className="text-sm text-red-700">{result?.payment_error}</p>
        )}
        <p className="text-sm text-gray-700">
          {failed ? k("payment.failedText") : k("payment.actionText")}
        </p>
      </div>
    );
  };

  let body: ReactNode = null;
  let footer: ReactNode = null;
  const closeButton = (label: string) => (
    <CustomButton
      title={label}
      onClick={onClose}
      disabled={isApplying || finishing}
    />
  );

  if (step === "loading") {
    body = (
      <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
        <span translate="no">
          <Spinner size="sm" />
        </span>
        {k("modal.loading")}
      </div>
    );
    footer = closeButton(t("common.cancel") as string);
  } else if (step === "address") {
    body = (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-700">{k("address.intro")}</p>
        <BillingAddressFields
          value={address}
          onChange={setAddress}
          showMissing={showMissing}
        />
      </div>
    );
    footer = (
      <>
        {closeButton(t("common.cancel") as string)}
        <CustomButton
          variant="primary"
          title={k("address.submit")}
          onClick={submitAddress}
          isLoading={isSavingAddress}
          disabled={isSavingAddress}
        />
      </>
    );
  } else if (step === "review" && preview) {
    body = isImmediate ? renderImmediate(preview) : renderPeriodEnd(preview);
    footer = (
      <>
        {closeButton(t("common.cancel") as string)}
        <CustomButton
          variant="primary"
          title={
            isImmediate
              ? k("modal.payAndSwitch", { plan: planName(target) })
              : k("modal.schedule", { plan: planName(target) })
          }
          onClick={confirm}
          isLoading={isApplying || finishing}
          disabled={isApplying || finishing || blockedByScheduled}
        />
      </>
    );
  } else if (step === "payment") {
    body = renderPayment();
    footer = (
      <>
        {closeButton(t("common.close") as string)}
        {result?.payment_error && (
          <CustomButton
            variant="secondary"
            title={k("payment.updateCard")}
            onClick={openCardUpdate}
            isLoading={isOpeningCardUpdate}
            disabled={isOpeningCardUpdate}
          />
        )}
        <CustomButton
          variant="secondary"
          title={k("payment.checkPayment")}
          onClick={checkPayment}
          isLoading={checking || finishing}
          disabled={checking || finishing}
        />
        {result?.hosted_invoice_url && (
          <CustomButton
            variant="primary"
            title={k("payment.payInvoice")}
            onClick={openInvoice}
          />
        )}
      </>
    );
  } else {
    body = null;
    footer = (
      <>
        {closeButton(t("common.close") as string)}
        <CustomButton
          variant="primary"
          title={t("common.retry") as string}
          onClick={() => runPreview(target)}
        />
      </>
    );
  }

  return (
    <ModalShell
      show
      onClose={() => {
        if (!isApplying && !finishing) onClose();
      }}
      size="lg"
      title={title}
      subtitle={k("modal.subtitle", { plan: planName(fromPlan) })}
      footer={footer}
      aria-label={title}
    >
      <div className="flex flex-col gap-4">
        {notice && renderNote("info", <p>{notice}</p>)}
        {body}
        {error && <p className="text-sm text-red-700">{error}</p>}
      </div>
    </ModalShell>
  );
};

export default PlanChangeModal;
